import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ElementRef, inject } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators, FormsModule } from "@angular/forms";
import { SharedMaterialModule } from "@config/shared-material.module";
import { ToastService } from "@services/toast.service";
import { Constants } from "@config/constants";
import { HttpService } from "@services/http.service";
import { IAppToken } from "@modals/api.modal";
import { Messages } from "@config/messages";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { Subscription, distinctUntilChanged } from "rxjs";
import { TableComponent } from "@components/layouts/table/table.component";
import { MatCardModule } from "@angular/material/card";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { EnumTableDynamicInputType, ITableActionRow, IUploadPDFUser, SIGNER_ALLOWED_ACTIONS_OPTION_LABELS, SIGNER_ALLOWED_ACTIONS_OPTIONS, DEFAULT_SIGNER_ALLOWED_ACTIONS, normalizeAllowedActionsForRole } from "@modals/app.modal";
import { TableService } from "@services/table.service";
import { SignService } from "@services/sign.service";
import { AuthService } from "@services/auth.service";
import { ActivatedRoute } from "@angular/router";
import { buildCountryOptions, CountryOption, handleCountrySelectPanelOpened } from "@config/country-options";
import { TranslateModule } from "@ngx-translate/core";
import { IUserDetails } from "@modals/app.modal";

@Component({
  selector: "app-create-setup-document",
  standalone: true,
  imports: [SharedMaterialModule, MatFormFieldModule, MatSelectModule, MatInputModule, NgxExtendedPdfViewerModule, TableComponent, MatCardModule, MatSlideToggleModule, FormsModule, TranslateModule],
  templateUrl: "./create-setup-document.component.html",
  styleUrl: "./create-setup-document.component.scss",
})
export class CreateSetupDocumentComponent implements OnDestroy {

  @Input() hideManageUsersSection = false;

  @Input() showImportedSignersPanel = false;

  @Input() useCombinedSetupLayout = false;


  @Input() wizardSetupPhase: 0 | 1 = 0;

  @Input() isSelfSignRequestPath = false;

  isDraftRequest = false;
  private _draftRequesterPrefilled = false;

  @Output() wizardSetupAdvance = new EventEmitter<void>();
  @Output() wizardSetupGoBack = new EventEmitter<void>();


  signerPanel: "requester" | "users" | "other" = "requester";


  signerValidationAttempted = false;

  /** When true, signers can be drag-reordered; when false, order column is hidden. */
  signingOrderEnabled = false;


  greetingName = "";

  requestId: string = "";


  greetingKey = "dashboardGreetingMorning";


  pdfFileSize = "";


  pdfFileDate = "";

  countryCode = "";
  countryOptions: CountryOption[] = [];
  filteredCountryOptions: CountryOption[] = [];
  selectedCountry: CountryOption = { name: "", iso2: "", dialCode: "", flag: "" };
  countrySearchControl = new FormControl("");

  @Output() pdfUploaded = new EventEmitter<File>();
  @Output() appTokenUpdated = new EventEmitter<IAppToken>();
  @Output() selfSignSelected = new EventEmitter<void>();
  @Output() onReset = new EventEmitter<void>();
  @Output() onNext = new EventEmitter<void>();

  private _httpService = inject(HttpService);
  private _authService = inject(AuthService);
  private _activatedRoute = inject(ActivatedRoute);
  private _appTokenSubscription!: Subscription;
  private _countrySearchSubscription!: Subscription;
  private _userDetailsSubscription?: Subscription;

  requesterForm!: FormGroup;
  /** Editable document name sent as API `docName` (defaults to uploaded file name). */
  docNameControl = new FormControl("", [Validators.required]);
  pdfFileName = "";
  pdfFile: File | null = null;
  pdfPreviewUrl: string | null = null;
  private _pdfPreviewObjectUrl: string | null = null;

  pdfUploadError = false;
  allTokens: IAppToken[] = [];
  appToken: FormControl;
  Messages = Messages;

  private static readonly EMAIL_REGEX = "^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$";


  private static requesterPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const raw = (control.value ?? "").toString();
    const digits = raw.replace(/\D/g, "");
    if (!digits.length) {
      return null;
    }
    if (digits.length < 6) {
      return { phoneShort: true };
    }
    if (digits.length > 15) {
      return { phoneLong: true };
    }
    return null;
  }

  tableColumns: Array<{
    columnDef: string; header: string; isSortable?: boolean; required?: boolean;
    isEditable?: boolean; inputType?: EnumTableDynamicInputType;
    dropdownOptions?: string[]; placeholder?: string; selectPlaceholder?: string;
    optionLabels?: Record<string, string>; searchPlaceholder?: string;
  }> = [
      { columnDef: "name", header: "name", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.TEXT, placeholder: "name" },
      { columnDef: "role", header: "role", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.DROPDOWN, dropdownOptions: ["signer", "reviewer", "observer"], selectPlaceholder: "role", optionLabels: { signer: "roleSigner", reviewer: "roleReviewer", observer: "roleObserver" } },
      { columnDef: "email", header: "email", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "email" },
      { columnDef: "emailCc", header: "emailCc", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "emailCc" },
      { columnDef: "phone", header: "phone", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.TEL, placeholder: "phone" },
      { columnDef: "allowedActions", header: "allowedActions", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.MULTI_DROPDOWN, dropdownOptions: [...SIGNER_ALLOWED_ACTIONS_OPTIONS], selectPlaceholder: "allowedActions", optionLabels: SIGNER_ALLOWED_ACTIONS_OPTION_LABELS },
    ];

  tableRowActions: ITableActionRow[] = [
    {
      icon: "delete", color: "warn", title: "delete",
      isDisabled: (): boolean => !(this.tableService.tableInputData?.length > 1),
      actionFn: (row: IUploadPDFUser) => {
        const i = this.tableService.tableInputData.findIndex((d) => d.tempID === row.tempID);
        this.tableService.tableInputData.splice(i, 1);
        this.tableService.syncSignerOrders();
        this.tableService.tableInputData = [...this.tableService.tableInputData];
      },
    },
  ];

  importedSignersTableColumns: Array<{
    columnDef: string;
    header: string;
    isSortable?: boolean;
    required?: boolean;
    isEditable?: boolean;
    inputType?: EnumTableDynamicInputType;
    dropdownOptions?: string[];
    placeholder?: string;
    selectPlaceholder?: string;
    optionLabels?: Record<string, string>;
    readOnly?: boolean;
  }> = [
      { columnDef: "name", header: "name", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.TEXT, placeholder: "name" },
      {
        columnDef: "role",
        header: "role",
        isSortable: false,
        required: true,
        isEditable: true,
        readOnly: true,
        inputType: EnumTableDynamicInputType.DROPDOWN,
        dropdownOptions: ["signer"],
        selectPlaceholder: "role",
        optionLabels: { signer: "roleSigner" },
      },
      { columnDef: "email", header: "email", isSortable: false, required: true, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "email" },
      { columnDef: "emailCc", header: "emailCc", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.EMAIL, placeholder: "emailCc" },
      { columnDef: "phone", header: "phone", isSortable: false, required: false, isEditable: true, inputType: EnumTableDynamicInputType.TEL, placeholder: "phone" },
      {
        columnDef: "allowedActions",
        header: "allowedActions",
        isSortable: false,
        required: false,
        isEditable: true,
        inputType: EnumTableDynamicInputType.MULTI_DROPDOWN,
        dropdownOptions: [...SIGNER_ALLOWED_ACTIONS_OPTIONS],
        selectPlaceholder: "allowedActions",
        optionLabels: SIGNER_ALLOWED_ACTIONS_OPTION_LABELS,
      },
    ];

  importedSignersRowActions: ITableActionRow[] = [
    {
      icon: "delete",
      color: "warn",
      title: "delete",
      isDisabled: (): boolean => !(this.tableService.tableInputData?.length > 1),
      actionFn: (row: IUploadPDFUser) => {
        const i = this.tableService.tableInputData.findIndex((d) => d.tempID === row.tempID);
        if (i >= 0) {
          this.tableService.tableInputData.splice(i, 1);
          this.tableService.syncSignerOrders();
          this.tableService.tableInputData = [...this.tableService.tableInputData];
        }
      },
    },
  ];

  constructor(
    private _tostr: ToastService,
    public tableService: TableService,
    private _signService: SignService
  ) {
    this.refreshGreeting();
    if (!this.tableService.tableInputData.length) {
      this.addNewUser();
    } else {
      this.tableService.syncSignerOrders();
    }
    this.normalizeReviewerAllowedActions();
    this.isDraftRequest = this._activatedRoute.snapshot.data["isDraftRequest"] ?? false;
    if (!this.isDraftRequest) {
      this.getAllTokens();
    }
    this.appToken = new FormControl("", [...(this.isDraftRequest ? [] : [Validators.required])]);
    this.initCountryOptions();

    this.requesterForm = new FormGroup({
      name: new FormControl("", Validators.required),
      email: new FormControl("", [Validators.required, Validators.pattern(CreateSetupDocumentComponent.EMAIL_REGEX)]),
      phone: new FormControl("", CreateSetupDocumentComponent.requesterPhoneValidator),
      emailSubject: new FormControl(""),
      message: new FormControl(""),
    });

    this._appTokenSubscription = this.appToken.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.appTokenUpdated.emit(this.appToken.value));

    this._userDetailsSubscription = this._authService.userDetails$.subscribe((u) => {
      if (u) this.prefillRequester(u);
    });
  }

  private prefillRequester(u: IUserDetails): void {
    if (this._draftRequesterPrefilled) return;
    const fullName = this.resolveLoggedInFullName(u);
    if (fullName) this.greetingName = fullName;
    const patch: { [k: string]: string } = {};
    const currentName = (this.requesterForm.get("name")?.value ?? "").toString().trim();
    const firstOnly = (u.FirstName ?? "").toString().trim();
    if (fullName && (!currentName || (firstOnly && currentName === firstOnly))) {
      patch["name"] = fullName;
    }
    if (!this.requesterForm.get('email')?.value && u.Email) patch['email'] = u.Email;

    // const rawPhone = (u.Mobile ?? '').toString().trim();
    // if (!this.requesterForm.get('phone')?.value && rawPhone) {
    //   const match = rawPhone.match(/^(\+\d{1,4})[-\s]?(.*)$/);
    //   const dialCode = (match?.[1] || '').trim();
    //   const localNumber = (match?.[2] || rawPhone).trim();
    //   if (dialCode) {
    //     const found = this.countryOptions.find((c) => (c.dialCode || '').trim() === dialCode);
    //     if (found) {
    //       this.selectedCountry = found;
    //       this.countryCode = found.dialCode || this.countryCode;
    //     }
    //   }
    //   patch['phone'] = localNumber;
    // }
    if (Object.keys(patch).length) this.requesterForm.patchValue(patch);
  }

  /** Resolved name for Sign/RequestStr and Sign/RequestAndUpload payloads. */
  resolveDocumentName(): string {
    const custom = (this.docNameControl.value ?? "").toString().trim();
    return custom || this.pdfFileName || "";
  }

  isDocumentStepReady(): boolean {
    if (!this.pdfFileName.length || this.docNameControl.invalid) {
      return false;
    }
    if (
      !this.useCombinedSetupLayout &&
      this.wizardSetupPhase === 0 &&
      !this.isSelfSignRequestPath
    ) {
      return true;
    }
    if (
      this.isSelfSignRequestPath &&
      !this.useCombinedSetupLayout &&
      this.wizardSetupPhase === 0
    ) {
      return this.appToken?.valid === true;
    }
    return this.isDraftRequest || !!this.appToken?.value;
  }


  isSignerStepReady(): boolean {
    return this.isRequesterFormValid() && this.areManageUsersValid();
  }

  private isRequesterFormValid(): boolean {
    const f = this.requesterForm;
    if (!f) return false;
    return (
      f.get("name")?.valid === true &&
      f.get("email")?.valid === true &&
      f.get("phone")?.valid === true
    );
  }

  private areManageUsersValid(): boolean {
    const rows = this.tableService.tableInputData ?? [];
    if (!rows.length) return false;
    return rows.every((item: IUploadPDFUser) => this.isManageUserRowValid(item));
  }

  private isManageUserRowValid(item: IUploadPDFUser): boolean {
    const nameOk = !!(item?.name || "").toString().trim();
    const email = (item?.email || "").toString().trim().toLowerCase();
    const emailOk = !!email && new RegExp(CreateSetupDocumentComponent.EMAIL_REGEX).test(email);
    const emailCc = (item?.emailCc || "").toString().trim().toLowerCase();
    const emailCcOk =
      !emailCc || new RegExp(CreateSetupDocumentComponent.EMAIL_REGEX).test(emailCc);
    const roleOk = !!(item?.role || "").toString().trim();
    const role = (item?.role ?? "").toString().trim().toLowerCase();
    const allowedActionsOk =
      role !== "signer" ||
      !!(item?.allowedActions ?? DEFAULT_SIGNER_ALLOWED_ACTIONS).toString().trim();
    const phoneDigits = (item?.phone ?? "").toString().replace(/\D/g, "");
    const phoneOk = !phoneDigits.length || (phoneDigits.length >= 6 && phoneDigits.length <= 15);
    return nameOk && emailOk && emailCcOk && phoneOk && roleOk && allowedActionsOk;
  }

  private isAppTokenValid(): boolean {
    return this.isDraftRequest || this.appToken?.valid === true;
  }

  requesterPanelHasErrors(): boolean {
    const f = this.requesterForm;
    if (!f) return false;
    return !!(
      (f.get("name")?.invalid && f.get("name")?.touched) ||
      (f.get("email")?.invalid && f.get("email")?.touched) ||
      (f.get("phone")?.invalid && f.get("phone")?.touched)
    );
  }

  manageUsersPanelHasErrors(): boolean {
    return (
      this.signerValidationAttempted &&
      (!this.hideManageUsersSection || this.showImportedSignersPanel) &&
      !this.areManageUsersValid()
    );
  }

  otherPanelHasErrors(): boolean {
    if (this.isDraftRequest) return false;
    return (this.appToken?.invalid && this.appToken?.touched) === true;
  }


  private revealInvalidSignerPanel(): void {
    if (!this.isRequesterFormValid()) {
      this.signerPanel = "requester";
      return;
    }
    if (
      (!this.hideManageUsersSection || this.showImportedSignersPanel) &&
      !this.areManageUsersValid()
    ) {
      this.signerPanel = "users";
      setTimeout(() => this.tableService.triggerTouchInputs(), 0);
      return;
    }
    if (!this.isAppTokenValid()) {
      this.signerPanel = "other";
    }
  }

  isNextEnabled(): boolean {
    return this.isDocumentStepReady() && this.isSignerStepReady();
  }


  handleNextClick(): void {
    if (!this.useCombinedSetupLayout && this.wizardSetupPhase === 0) {
      if (!this.isDraftRequest) {
        this.appToken?.markAsTouched();
        this.appToken?.updateValueAndValidity({ emitEvent: false });
      }
      this.docNameControl.markAsTouched();
      const hasPdf = this.pdfFileName.length > 0;
      this.pdfUploadError = !hasPdf;
      if (!this.isDocumentStepReady()) return;
      this.pdfUploadError = false;

      if (this.isSelfSignRequestPath) {
        this.startSelfSign();
        return;
      }

      this.wizardSetupAdvance.emit();
      return;
    }

    this.requesterForm?.markAllAsTouched();
    this.docNameControl.markAsTouched();
    if (!this.isDraftRequest) {
      this.appToken?.markAsTouched();
      this.appToken?.updateValueAndValidity({ emitEvent: false });
    }
    if (this.signerPanel === "users") {
      this.tableService.triggerTouchInputs();
    }
    const hasPdf = this.pdfFileName.length > 0;
    this.pdfUploadError = !hasPdf;
    if (this.isNextEnabled()) {
      this.pdfUploadError = false;
      this.onNext.emit();
      return;
    }
    if (!this.useCombinedSetupLayout && this.wizardSetupPhase === 1) {
      this.signerValidationAttempted = true;
      this.revealInvalidSignerPanel();
    }
  }


  applyPendingPdf(file: File | null): void {
    if (!file || file.type !== "application/pdf") return;
    this.applyUploadedPdf(file);
  }

  applyDraftPrefill(
    requesterDetails: { name?: string; email?: string; phone?: string; requesterId?: string; },
    file: File,
    docName?: string
  ): void {
    console.log("ffnn,mgng gngmgng ggmngg ", requesterDetails)
    this.requestId = requesterDetails.requesterId ?? "";

    console.log("Request ID:", this.requestId);
    const rawPhone = (requesterDetails?.phone ?? "").toString().trim();
    const match = rawPhone.match(/^(\+\d{1,4})[-\s]?(.*)$/);
    const dialCode = (match?.[1] || "").trim();
    const localNumber = (match?.[2] || rawPhone).trim();
    if (dialCode) {
      const found = this.countryOptions.find((c) => (c.dialCode || "").trim() === dialCode);
      if (found) {
        this.selectedCountry = found;
        this.countryCode = found.dialCode || this.countryCode;
      }
    }
    this.requesterForm.patchValue({
      name: requesterDetails?.name ?? "",
      email: requesterDetails?.email ?? "",
      phone: localNumber,
    });
    this._draftRequesterPrefilled = true;
    this.applyUploadedPdf(file, docName ?? file.name);
  }

  private applyUploadedPdf(file: File, docName?: string): void {
    this.syncPdfPreview(file);
    this.pdfFileName = file.name;
    this.docNameControl.setValue(docName ?? file.name);
    this.rememberFileMeta(file);
    this.pdfUploadError = false;
    this.pdfUploaded.emit(file);
  }


  resetSignerStep(): void {
    this.signerValidationAttempted = false;
    this.signerPanel = "requester";
    this.requesterForm.reset({
      name: "",
      email: "",
      phone: "",
      emailSubject: "",
      message: "",
    });
    this.tableService.tableInputData = [
      this.createEmptyUser(),
    ];
    this.signingOrderEnabled = false;
    if (this.allTokens?.length) {
      this.appToken.setValue(this.allTokens[0]);
    } else {
      this.appToken.reset("");
    }
    const user = this._authService.getUserDetails();
    if (user) {
      this.prefillRequester(user);
    }
  }

  clearPdf(): void {
    this.pdfFileName = "";
    this.docNameControl.reset("");
    this.syncPdfPreview(null);
    this.pdfFileSize = "";
    this.pdfFileDate = "";
    this.pdfUploadError = false;
  }

  private syncPdfPreview(file: File | null): void {
    this.revokePdfPreviewUrl();
    this.pdfFile = file;
    if (!file) {
      this.pdfPreviewUrl = null;
      return;
    }
    this._pdfPreviewObjectUrl = URL.createObjectURL(file);
    this.pdfPreviewUrl = this._pdfPreviewObjectUrl;
  }

  private revokePdfPreviewUrl(): void {
    if (this._pdfPreviewObjectUrl) {
      URL.revokeObjectURL(this._pdfPreviewObjectUrl);
      this._pdfPreviewObjectUrl = null;
    }
    this.pdfPreviewUrl = null;
  }

  private initCountryOptions(): void {
    this.countryOptions = buildCountryOptions("en");
    this.filteredCountryOptions = [...this.countryOptions];
    this.selectedCountry = this.countryOptions.find((c) => c.iso2 === "IN") || this.countryOptions[0];
    this.countryCode = this.selectedCountry?.dialCode || "";
    this._countrySearchSubscription = this.countrySearchControl.valueChanges.subscribe((term) => {
      const v = (term || "").toString().toLowerCase().trim();
      this.filteredCountryOptions = !v ? [...this.countryOptions] : this.countryOptions.filter((c) => c.name.toLowerCase().includes(v) || c.dialCode.includes(v) || c.iso2.toLowerCase().includes(v));
    });
  }

  onCountrySelect(country: CountryOption): void {
    this.selectedCountry = country;
    this.countryCode = country?.dialCode || "";
  }

  onCountryPanelOpened(opened: boolean): void {
    handleCountrySelectPanelOpened(opened, () => this.countrySearchControl.setValue("", { emitEvent: true }));
  }

  addNewUser(): void {
    this.tableService.tableInputData = [
      ...this.tableService.tableInputData,
      this.createEmptyUser(),
    ];
    this.tableService.syncSignerOrders();
  }

  addMe(): void {
    const user = this._authService.getUserDetails();
    if (!user?.Email) {
      this._tostr.showWarning("Unable to add yourself. User profile not found.");
      return;
    }

    const email = user.Email.trim();
    const fullName = this.resolveLoggedInFullName(user);
    const rows = this.tableService.tableInputData.length
      ? [...this.tableService.tableInputData]
      : [this.createEmptyUser()];

    const firstRow = rows[0];
    const isFirstRowEmpty =
      !(firstRow?.name || "").trim() && !(firstRow?.email || "").trim();

    if (isFirstRowEmpty) {
      rows[0] = {
        ...firstRow,
        name: fullName,
        email,
        phone: "",
        role: "signer",
        allowedActions: firstRow.allowedActions || DEFAULT_SIGNER_ALLOWED_ACTIONS,
      };
    } else {
      const me = this.createEmptyUser();
      me.name = fullName;
      me.email = email;
      me.phone = "";
      me.role = "signer";
      me.allowedActions = DEFAULT_SIGNER_ALLOWED_ACTIONS;
      rows.push(me);
    }

    this.tableService.tableInputData = [...rows];
    this.tableService.syncSignerOrders();
  }

  private resolveLoggedInFullName(user: IUserDetails): string {
    return [user.FirstName, user.LastName]
      .map((part) => (part ?? "").toString().trim())
      .filter(Boolean)
      .join(" ")
      .trim();
  }


  setupSelfSignMode(): void {
    const user = this._authService.getUserDetails();
    if (user) {
      this.prefillRequester(user);
    }
    if (!user?.Email) {
      this._tostr.showWarning("Unable to start self sign. User profile not found.");
      return;
    }

    const fullName = this.resolveLoggedInFullName(user);
    this.tableService.tableInputData = [
      {
        name: fullName,
        email: user.Email.trim(),
        emailCc: "",
        phone: "",
        role: "signer",
        allowedActions: DEFAULT_SIGNER_ALLOWED_ACTIONS,
        order: 1,
        tempID: this._signService.generateUUID(),
      },
    ];
  }

  getSelfSignUserLabel(): string {
    const user = this._authService.getUserDetails();
    if (!user?.Email) return "";
    const fullName = this.resolveLoggedInFullName(user);
    return fullName || user.Email;
  }

  getSelfSignUserEmail(): string {
    return this._authService.getUserDetails()?.Email?.trim() ?? "";
  }

  getSelectedAppTokenLabel(): string {
    const token = this.appToken?.value as IAppToken | null;
    if (!token?.AppName) return "";
    return `${token.AppName} (${token.Token})`;
  }

  startSelfSign(): void {
    this.appToken?.markAsTouched();
    this.appToken?.updateValueAndValidity({ emitEvent: false });
    const hasPdf = this.pdfFileName.length > 0;
    this.pdfUploadError = !hasPdf;
    if (!hasPdf) {
      return;
    }
    if (!this.appToken?.value) {
      this._tostr.showWarning("Please select an app token before self sign.");
      return;
    }
    this.setupSelfSignMode();
    this.pdfUploadError = false;
    this.selfSignSelected.emit();
  }

  get uploadNextLabelKey(): string {
    return this.isSelfSignRequestPath ? "selfSignContinue" : "sendForESignature";
  }

  private createEmptyUser(): IUploadPDFUser {
    return {
      name: "",
      email: "",
      emailCc: "",
      phone: "",
      role: this.showImportedSignersPanel ? "signer" : "",
      allowedActions: DEFAULT_SIGNER_ALLOWED_ACTIONS,
      order: this.tableService.tableInputData.length + 1,
      tempID: this._signService.generateUUID(),
    };
  }

  private normalizeReviewerAllowedActions(): void {
    let changed = false;
    for (const row of this.tableService.tableInputData) {
      if ((row.role ?? "").toString().trim().toLowerCase() !== "reviewer") {
        continue;
      }
      const next = normalizeAllowedActionsForRole(row.role, row.allowedActions);
      if ((row.allowedActions ?? "").toString() !== next) {
        row.allowedActions = next;
        changed = true;
      }
    }
    if (changed) {
      this.tableService.replaceTableInputData([...this.tableService.tableInputData]);
    }
  }

  getAllTokens(): void {
    this._httpService
      .get(
        this._httpService.createEndpoint(Constants.API_URI_APP_TOKEN_GET_ALL, undefined, {
          GetActiveToken: true,
        })
      )
      .subscribe(
        (res: any) => {
          this.allTokens = res.Data;

          if (!this.appToken.value && this.allTokens?.length) {
            this.appToken.setValue(this.allTokens[0]);
          }
        },
        () => { }
      );
  }

  private refreshGreeting(): void {
    const h = new Date().getHours();
      this.greetingKey = h < 12 ? "dashboardGreetingMorning" :
        h < 17 ? "dashboardGreetingAfternoon" :
          "dashboardGreetingEvening";

  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let val = bytes;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i++;
    }
    return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  }

  private static readonly MONTH_LABELS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  private formatMonthDay(d: Date): string {
    const m = CreateSetupDocumentComponent.MONTH_LABELS[d.getMonth()] ?? "";
    return `${m} ${d.getDate()}`;
  }

  private rememberFileMeta(file: File): void {
    this.pdfFileSize = this.formatBytes(file.size);
    this.pdfFileDate = this.formatMonthDay(file.lastModified ? new Date(file.lastModified) : new Date());
  }

  onPdfUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.type !== "application/pdf") { this._tostr.showWarning("Only PDF files are allowed"); return; }
      this.applyUploadedPdf(file);
      input.value = "";
    }
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (file.type !== "application/pdf") { this._tostr.showWarning("Only PDF files are allowed"); return; }
      this.applyUploadedPdf(file);
    }
  }

  allowDrop(event: DragEvent): void { event.preventDefault(); }



  @ViewChild("cloudFileInput") cloudFileInput!: ElementRef<HTMLInputElement>;

  private static readonly CLOUD_CONFIG = {
    GOOGLE_API_KEY: "",
    GOOGLE_CLIENT_ID: "",
    DROPBOX_APP_KEY: "",
    ONEDRIVE_CLIENT_ID: "",
  };







  ngOnDestroy(): void {
    this.revokePdfPreviewUrl();
    this._appTokenSubscription?.unsubscribe();
    this._countrySearchSubscription?.unsubscribe();
    this._userDetailsSubscription?.unsubscribe();
  }
}



